import React from 'react';
import { Button } from '@/components/ui/button';
import { Settings, Eye, EyeOff, GripVertical, Maximize2, Minimize2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

/* Widths are literal so the size you pick in Customize is the size you get.
   Every card shares one fixed height (--dash-card-h). */
const CARD_H = 'lg:h-[var(--dash-card-h)]';
const SIZE_CLASSES = {
  small: `basis-full lg:basis-[calc(33.333%-13.34px)] ${CARD_H}`,
  large: `basis-full lg:basis-[calc(66.666%-6.67px)] ${CARD_H}`,
  full: `basis-full ${CARD_H}`,
};
const SIZE_LABEL = { small: '1/3', large: '2/3', full: 'Full' };

function SortableWidget({ id, children, isCustomizing, size, onResize }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={`${SIZE_CLASSES[size] || 'col-span-1'} min-w-0 relative`} data-testid={`widget-${id}`}>
      {isCustomizing && (
        <div className="absolute -top-2 right-1 z-10 flex items-center gap-1">
          <button onClick={onResize} data-testid={`resize-widget-${id}`}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm text-[10px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            {size === 'full' ? <Minimize2 size={10} /> : <Maximize2 size={10} />}
            {SIZE_LABEL[size]}
          </button>
          <button {...attributes} {...listeners} data-testid={`drag-widget-${id}`}
            className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing transition-colors">
            <GripVertical size={12} />
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

export function DashboardCustomizeBar({ isCustomizing, setIsCustomizing, widgetList, visibility, toggleWidget, extraButtons }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 shrink-0">
      {extraButtons}
      <Button data-testid="toggle-customize-btn" variant={isCustomizing ? 'default' : 'outline'} size="sm"
        className={`text-sm h-8 ${isCustomizing ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200' : 'border-gray-200 dark:border-gray-700'}`}
        onClick={() => setIsCustomizing(!isCustomizing)}>
        <Settings size={14} className="mr-1.5" /><span className="hidden sm:inline">{isCustomizing ? (t('done') || 'Done') : (t('customize') || 'Customize')}</span>
      </Button>
    </div>
  );
}

export function DashboardCustomizePanel({ isCustomizing, widgetList, visibility, toggleWidget }) {
  const { t } = useTranslation();
  if (!isCustomizing) return null;
  return (
 <div data-testid="customize-panel" className="flex flex-wrap gap-4 p-5 bg-white dark:bg-[var(--surface)] border-0 rounded-card shadow-card justify-center items-center">
      <span className="text-sm text-gray-600 dark:text-gray-300 font-medium w-full text-center mb-2">Show / Hide Widgets — Drag to Reorder</span>
      {widgetList.map(w => (
        <button key={w.key} onClick={() => toggleWidget(w.key)} data-testid={`toggle-widget-${w.key}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
            visibility[w.key]
              ? 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
              : 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400'
          }`}>
          {visibility[w.key] ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-gray-400" />}
          {w.label}
        </button>
      ))}
    </div>
  );
}

export function DashboardGrid({ visibleWidgets, isCustomizing, widgetSizes, resizeWidget, handleDragEnd, renderWidget }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  if (visibleWidgets.length === 0) {
    return (
      <p data-testid="dashboard-no-widgets" className="text-sm text-gray-400 py-10 text-center">
        No dashboard cards are enabled for your role.
      </p>
    );
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={visibleWidgets} strategy={verticalListSortingStrategy}>
        <div className="flex flex-wrap gap-5 items-start pb-12">
          {visibleWidgets.map(key => (
            <SortableWidget key={key} id={key} isCustomizing={isCustomizing} size={widgetSizes[key] || 'full'} onResize={() => resizeWidget(key)}>
              {renderWidget(key, widgetSizes[key] || 'full')}
            </SortableWidget>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
